import type { StreamingTagState } from "@/lib/game/model";
import { lastLlmError } from "./uistate";
import { persistentSignal } from "@/lib/persistentsignal";
import { signal } from "@preact/signals-react";
import { lastTurnInput, lastTurnLength } from "@/lib/game/rewind";
import { checkpointFromUrl, loadCheckpoint } from "./checkpoints";
import { model } from "./model";
import { readSse } from "@/lib/ssestream";
import type { StoryEventType } from "@/lib/types";
import type { UsageRecordType } from "@/lib/usage";

/**
 * Talking to the session server.
 *
 * Server-side play splits a turn in two: `delta` events carry narrative text as
 * the model produces it (provisional, for display), and one `events` message
 * carries the authoritative story events that were appended to the session log.
 * The client shows the former and folds in the latter — stream for feel, events
 * for truth.
 *
 * The API is gated by Cloudflare Access, which the browser satisfies through its
 * normal session cookie, so requests just need `credentials: "include"`.
 */

export interface TurnHandlers {
  onDelta?: (state: StreamingTagState) => void;
  onEvents?: (events: StoryEventType[]) => void;
  onError?: (message: string) => void;
}

function api(path: string, session: string): string {
  return `/api/${path}?session=${encodeURIComponent(session)}`;
}

/**
 * Claim a session on the server.
 *
 * There is no owner to send: the server takes that from the Access-verified
 * identity on the request. `credentials: "include"` is what carries the Access
 * cookie.
 */
export async function createSession(session: string): Promise<void> {
  const response = await fetch(api("create", session), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: "{}",
  });
  if (!response.ok) {
    throw new Error(`Could not create session: ${response.status}`);
  }
}

/** The session's events from a cursor — how a reconnecting client catches up. */
export async function fetchEvents(
  session: string,
  since = 0,
): Promise<{ total: number; events: StoryEventType[] }> {
  const response = await fetch(api("events", session) + `&since=${since}`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Could not read session: ${response.status}`);
  }
  return (await response.json()) as { total: number; events: StoryEventType[] };
}

/** Play a turn, reporting text as it arrives and the events when it lands. */
export function sendInput(
  session: string,
  text: string,
  handlers: TurnHandlers = {},
): Promise<StoryEventType[]> {
  return streamTurn("input", session, { text }, handlers);
}

/**
 * Start the game on the server.
 *
 * A new session's log is empty until this runs: the opening description and
 * Ama's first words are generated by the engine, which lives on the server. The
 * client cannot produce them and must not try.
 *
 * 409 means the session already started — two tabs, or a reload arriving while
 * the first launch was still streaming. That is a normal race and not an error
 * to show anyone; the events are already on their way to the other caller and
 * this one picks them up on its next read.
 */
export async function launchSession(
  session: string,
  handlers: TurnHandlers = {},
): Promise<StoryEventType[]> {
  return streamTurn("launch", session, {}, handlers, [409]);
}

async function streamTurn(
  action: string,
  session: string,
  body: unknown,
  handlers: TurnHandlers,
  quiet: number[] = [],
): Promise<StoryEventType[]> {
  const response = await fetch(api(action, session), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (quiet.includes(response.status)) {
    return [];
  }
  if (!response.ok || !response.body) {
    const detail = await response.text().catch(() => "");
    // A refusal the server wrote for the player — currently the spending limit
    // — is already a sentence. Wrapping it in "Turn failed: 429" would bury the
    // one part they can act on.
    let message = `Turn failed: ${response.status} ${detail.slice(0, 200)}`;
    try {
      const { error } = JSON.parse(detail) as { error?: string };
      if (error) {
        message = error;
      }
    } catch {
      // Not JSON; the status line above is the best available.
    }
    handlers.onError?.(message);
    throw new Error(message);
  }
  let events: StoryEventType[] = [];
  for await (const message of readSse(response.body)) {
    if (message.event === "delta") {
      handlers.onDelta?.(JSON.parse(message.data) as StreamingTagState);
    } else if (message.event === "events") {
      events = JSON.parse(message.data) as StoryEventType[];
      handlers.onEvents?.(events);
    } else if (message.event === "error") {
      const { message: detail } = JSON.parse(message.data) as {
        message: string;
      };
      handlers.onError?.(detail);
    }
  }
  return events;
}

// --- Who the server thinks you are -------------------------------------------

export interface AuthState {
  /** Which identity source this deployment uses. */
  mode: "access" | "google" | "dev" | "none";
  /** The verified address, or null when signed out. */
  email: string | null;
  /** Where to send the browser to sign in, when that's a thing here. */
  loginUrl: string | null;
}

/**
 * Who the server says this is, once asked.
 *
 * A signal rather than a per-component fetch because more than one place needs
 * it, and the interesting one isn't Settings: a first-time visitor with no
 * model key of their own hits the "no OpenRouter key" error before they have
 * any reason to open Settings, and that error is exactly where signing in is
 * the answer.
 */
export const authState = signal<AuthState | null>(null);

/** Ask once, at startup. Failure leaves it null, which shows nothing. */
export async function loadAuth(): Promise<void> {
  sessionStatus.value = "Connecting...";
  authState.value = await fetchAuth();
}

/**
 * What the client is waiting for, in words, or null when it isn't waiting.
 *
 * Loading used to be invisible because it was instant: the log came out of
 * browser storage and the game was on screen before anything was fetched. Now
 * it is three round trips and a model call — who you are, which game, its log,
 * and for a new game the opening itself — and all of it happened behind a blank
 * screen.
 */
export const sessionStatus = signal<string | null>("Loading...");

/**
 * Ask the server who this is.
 *
 * Outside the API gate on purpose: a signed-out client has to be able to ask,
 * or it can't know to offer a sign-in. It answers with an identity and never a
 * credential.
 *
 * A deployment with no identity source at all answers `none`, which is the
 * local-play case — there is no server to sign in to and nothing to show.
 */
export async function fetchAuth(): Promise<AuthState> {
  try {
    const response = await fetch("/api/auth", { credentials: "include" });
    if (!response.ok) {
      return { mode: "none", email: null, loginUrl: null };
    }
    return (await response.json()) as AuthState;
  } catch {
    return { mode: "none", email: null, loginUrl: null };
  }
}

/** Sign in, coming back to where the player was. */
export function signIn(loginUrl: string): void {
  const next = window.location.pathname + window.location.search;
  window.location.href = `${loginUrl}?next=${encodeURIComponent(next)}`;
}

export function signOut(): void {
  const next = window.location.pathname + window.location.search;
  window.location.href = `/auth/logout?next=${encodeURIComponent(next)}`;
}

// --- The player's games on the server ----------------------------------------

export interface ServerSession {
  id: string;
  title: string;
  created: string;
  /** How far along it is; read from the game itself, not a cached count. */
  events: number;
}

/**
 * Every game this identity has on the server.
 *
 * Sessions were always addressable — a game's Durable Object is named after the
 * verified email and the session id — but nothing kept a list, and a DO
 * namespace can't be enumerated. So one game per browser, and clearing the id
 * lost it. The server keeps the list now; see worker/sessionindex.ts.
 */
export async function listServerSessions(): Promise<ServerSession[]> {
  const response = await fetch("/api/sessions", { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Could not list games: ${response.status}`);
  }
  return ((await response.json()) as { sessions: ServerSession[] }).sessions;
}

/** Start a new game on the server and switch this tab to it. */
export async function newServerSession(title?: string): Promise<ServerSession> {
  const response = await fetch("/api/sessions", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ title }),
  });
  if (!response.ok) {
    const { error } = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(error || `Could not start a game: ${response.status}`);
  }
  return ((await response.json()) as { session: ServerSession }).session;
}

export async function renameServerSession(
  id: string,
  title: string,
): Promise<void> {
  const response = await fetch("/api/sessions", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id, title, rename: true }),
  });
  if (!response.ok) {
    throw new Error(`Could not rename: ${response.status}`);
  }
}

/**
 * Delete a game on the server, for good.
 *
 * The log and any stored credential go with it. If it's the game this tab is
 * playing, the tab drops back to local play rather than sitting on an id that
 * now addresses an empty session.
 */
export async function deleteServerSession(id: string): Promise<void> {
  const response = await fetch(
    `/api/sessions?session=${encodeURIComponent(id)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!response.ok) {
    throw new Error(`Could not delete: ${response.status}`);
  }
  if (remoteSession.value === id) {
    remoteSession.value = null;
  }
}

/** What this server session has spent; see lib/usage.ts. */
export async function fetchServerUsage(
  session: string,
): Promise<UsageRecordType[]> {
  const response = await fetch(api("usage", session), {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Could not read usage: ${response.status}`);
  }
  return ((await response.json()) as { usage: UsageRecordType[] }).usage;
}

// --- Choosing where a turn runs ----------------------------------------------

/**
 * The server session to play in, or null to run the engine locally in this tab.
 *
 * Local play is the original mode and still works with a user's own OpenRouter
 * key. Setting a session id switches to server-side play, where the engine runs
 * in a Durable Object and this tab becomes a renderer.
 */
export const remoteSession = persistentSignal<string | null>(
  "remoteSession",
  null,
);

/**
 * Whether a turn can go anywhere, and what to say if not.
 *
 * There used to be two answers, because there were two engines: the browser
 * could play on the player's own key, or the server could play on its own. So
 * this had to know which mode the tab was in and what each needed. There is one
 * engine now, on the server, and the only question left is whether this tab has
 * a game there.
 */
export interface Playable {
  ok: boolean;
  /** Why not, as a sentence to show. Empty when ok. */
  why: string;
  /** Signing in would fix it, and here's where. */
  loginUrl: string | null;
  /**
   * Whether this is a wait rather than a problem.
   *
   * The composer refuses either way, but only one of them is worth offering
   * buttons about.
   */
  waiting?: boolean;
}

export function playable({
  auth,
  session,
  status,
}: {
  auth: AuthState | null;
  session: string | null;
  /** What the client is loading, if anything; see `sessionStatus`. */
  status?: string | null;
}): Playable {
  // Before the answer arrives, assume yes. It resolves in a moment, and a
  // composer that refuses and then changes its mind is worse than one that
  // accepts a keystroke early.
  if (!auth) {
    return { ok: true, why: "", loginUrl: null };
  }
  if (auth.loginUrl && !auth.email) {
    return {
      ok: false,
      why: "Sign in to play.",
      loginUrl: auth.loginUrl,
    };
  }
  // Still loading, whatever stage of it. Ahead of the session check because a
  // session id is in hand well before its log has been read — so the composer
  // was live over an empty world, and a turn sent then would have been played
  // against a game this tab had not seen. It also stops "try reloading" being
  // shown during the ordinary case of loading, which was both wrong and the one
  // action that would start the wait over.
  if (status) {
    return { ok: false, why: status, loginUrl: null, waiting: true };
  }
  if (!session) {
    return {
      ok: false,
      why: "No game on the server yet — try reloading.",
      loginUrl: null,
    };
  }
  return { ok: true, why: "", loginUrl: null };
}

/**
 * Play one turn, wherever this game runs.
 *
 * The two modes differ only in who generates the events: locally the engine
 * produces them here; remotely the server does and this folds them in. Either
 * way they land in the same log and render the same way.
 */
export async function playTurn(text: string): Promise<string | undefined> {
  const session = remoteSession.value;
  if (!session) {
    lastLlmError.value = "No game on the server yet — try reloading.";
    return undefined;
  }
  // Commands are instructions to the game rather than things the player said,
  // so they aren't echoed back as dialogue.
  pendingInput.value = text.startsWith("/") ? null : text;
  try {
    await intoModel((handlers) => sendInput(session, text, handlers));
  } finally {
    pendingInput.value = null;
  }
  return undefined;
}

/**
 * Whether a turn is in flight on the server.
 *
 * The UI used to read `model.runningSignal`, which is the engine's promise
 * queue — and the browser's engine hasn't run anything since the server took
 * it over, so that signal has been permanently false. Everything hanging off it
 * silently stopped: the composer never disabled, the throbber never appeared,
 * the transcript never scrolled. Pressing enter looked like it had done
 * nothing until the whole turn landed at once.
 */
export const turnRunning = signal(false);

/**
 * What the player just submitted, until the turn's real events arrive.
 *
 * The server appends the player's line to the log along with everything it
 * caused, so nothing at all appears for as long as a turn takes. This is shown
 * in its place and replaced by the authoritative event.
 */
export const pendingInput = signal<string | null>(null);

/**
 * Run a server turn into this tab's world.
 *
 * The deltas are provisional text to show while it arrives; the events are what
 * actually happened and are folded in. Shared by playing a turn and by starting
 * the game, which differ only in what the server is asked to do.
 */
async function intoModel(
  run: (handlers: TurnHandlers) => Promise<StoryEventType[]>,
): Promise<void> {
  turnRunning.value = true;
  try {
    await run({
      onDelta: (state) => {
        model.streaming.value = state;
      },
      onEvents: (events) => {
        model.streaming.value = null;
        // The real events are here, so the provisional echo has been superseded.
        pendingInput.value = null;
        model.appendRemoteEvents(events);
      },
      onError: (message) => {
        lastLlmError.value = message;
      },
    });
  } finally {
    model.streaming.value = null;
    turnRunning.value = false;
  }
}

/**
 * Undo the last turn, wherever this game runs.
 *
 * Locally that is a direct call on the model. Remotely it has to go through the
 * same path as a turn: the server owns the log, and a client that appends its
 * own rewind marker would hide a turn the server still believes in — they would
 * disagree on the next move, and a reload would bring the undone turn back.
 *
 * Returns what the player typed on the undone turn, which the input box puts
 * back so they can rephrase. That is read from the local mirror of the log in
 * both modes, because the client has it either way.
 */
export async function undoTurn(): Promise<string> {
  const text = lastTurnInput(model.liveUpdates.value);
  if (!remoteSession.value) {
    return "";
  }
  if (!text && !lastTurnLength(model.liveUpdates.value)) {
    return "";
  }
  await playTurn("/undo");
  return text;
}

/** Undo the last turn and immediately replay it, so a reroll is one click. */
export async function redoTurn(): Promise<void> {
  const text = await undoTurn();
  if (text) {
    await playTurn(text);
  }
}

/**
 * Start a fresh game on the server and switch this tab to it.
 *
 * "New Game", `/reset` and `/restart` all used to call `model.reset()`, which
 * empties the browser's copy of the world and re-launches the engine in the
 * tab. There is no engine in the tab: it threw, the transcript was gone, and
 * the server still held the game that was actually being played.
 */
export async function startNewGame(): Promise<void> {
  sessionStatus.value = "Starting a new game...";
  try {
    const created = await newServerSession();
    // Cleared before the switch so the old game doesn't sit there looking
    // current while the new one is being made.
    model.adoptRemoteLog([]);
    remoteSession.value = created.id;
  } catch (e) {
    sessionStatus.value = null;
    lastLlmError.value = `Could not start a game on the server: ${String(e)}`;
    return;
  }
  await initSession();
}

/**
 * Put an existing log — a saved game, a checkpoint — into a new server session.
 *
 * Loading one used to be `replaceLog` on the browser's world, from back when
 * that world was the game. Now the server owns it, so the tab would show the
 * loaded game while the server went on believing in the old one, and the next
 * turn snapped back. Loading means handing the log to a new session.
 */
export async function importGame(events: StoryEventType[]): Promise<void> {
  sessionStatus.value = "Loading that game...";
  try {
    const created = await newServerSession();
    await createSession(created.id);
    const response = await fetch(api("import", created.id), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ events }),
    });
    if (!response.ok) {
      throw new Error(`import failed: ${response.status}`);
    }
    model.adoptRemoteLog(events);
    remoteSession.value = created.id;
  } catch (e) {
    lastLlmError.value = `Could not load that game: ${String(e)}`;
  } finally {
    sessionStatus.value = null;
  }
}

/**
 * Start play in whichever mode this tab is in.
 *
 * Locally that means launching the game here. Remotely it means adopting the
 * server's log — the client must not launch, because the server owns the
 * session and a local launch would duplicate its events and fire LLM calls this
 * tab isn't paying for.
 */
export async function initSession(): Promise<void> {
  // Signed in and no game yet: start one on the server. Server play is what
  // signing in is for, and the alternative — dropping a signed-in player into
  // local play, where the first turn fails for want of an OpenRouter key — is
  // the confusion this is meant to remove.
  //
  // Keyed on having an identity, not on there being a sign-in button. Local
  // development identifies the player from DEV_IDENTITY and so has no loginUrl,
  // and requiring one meant `pnpm dev` came up with no game and a composer that
  // said "No game on the server yet — try reloading", forever. A deployment with
  // no identity source at all has no email either, so it still creates nothing.
  const auth = authState.value;
  if (!remoteSession.value && auth?.email) {
    sessionStatus.value = "Starting a new game...";
    try {
      const created = await newServerSession();
      remoteSession.value = created.id;
    } catch (e) {
      lastLlmError.value = `Could not start a game on the server: ${String(e)}`;
    }
  }

  const session = remoteSession.value;
  if (!session) {
    sessionStatus.value = null;
    // ?checkpoint=briefed drops straight into a recorded state, so a link can
    // point at a specific part of the game. Only in local play: in a server
    // session the log belongs to the server, and loading one here would put the
    // two out of step.
    const checkpoint = checkpointFromUrl();
    if (checkpoint) {
      try {
        await loadCheckpoint(checkpoint);
        return;
      } catch (e) {
        lastLlmError.value = String(e);
      }
    }
    model.checkLaunch();
    return;
  }
  try {
    sessionStatus.value = "Loading your game...";
    await createSession(session);
    const { events } = await fetchEvents(session);
    model.adoptRemoteLog(events);
    // An empty log is a game that hasn't started. Only the server can start it,
    // and until it does there is nothing to read: the player sees a text box
    // and no game.
    if (!events.length) {
      // The longest wait of the lot, because it is a model call rather than a
      // fetch. It clears as soon as the first words arrive.
      sessionStatus.value = "Starting the game...";
      await intoModel((handlers) => launchSession(session, handlers));
    }
  } catch (e) {
    lastLlmError.value = `Could not join session: ${String(e)}`;
  } finally {
    sessionStatus.value = null;
  }
}
